import * as Yup from "yup";
import moment from "moment";

interface ValidateDatesParams {
	value: string;
	subtype: DataSubtypes;
	customValidator?: (date: string[]) => boolean;
}

export enum DataSubtypes {
	"year",
	"month",
	"month/year",
	"date/month/year",
}

function getOnlyDateNumbers(date: string) {
	return date
		.split("")
		.filter((char) => !isNaN(parseFloat(char)) && isFinite(Number(char)));
}

export function validateDate({ value, subtype }: ValidateDatesParams) {
	// 	1 - Mês ou Ano
	// 2 - Mes/Ano
	// 3 - Completa
	// 3 -
	// Data de Emissão: Antes de hoje
	// Data de Validade: Depois de hoje
	// Data de admissão: Antes de hoje
	// 2 -
	// Comprovante de residência: Pode ser no máximo no mês anterior (Se ligar na validação do ano);
	// Data de comprovante de renda: ///DEPOIS VER ISSO
	// Renda informal --> Data de início da atividade: Máximo de 1 mes antes;

	const transformedValue = value?.search("_");

	if (transformedValue !== -1 || !value) {
		//Se não entrar aqui é porque a data está completa
		return false;
	}

	let date;
	switch (subtype) {
		case DataSubtypes.year:
			date = moment(value, "YYYY", true);
			break;

		case DataSubtypes.month:
			date = moment(value, "MM", true);
			break;

		case DataSubtypes["month/year"]:
			date = moment(value, "MM/YYYY", true);
			break;
		case DataSubtypes["date/month/year"]:
			date = moment(value, "DD/MM/YYYY", true);
			break;
	}

	return date.isValid() ? date.toDate() : false;
}

export const schema = Yup.object().shape({
	nomeCompleto: Yup.string()
		.required("Digite o nome completo do proponente")
		.min(5, "É necessário pelo menos 5 caracteres")
		.matches(/[^A-Za-z]/g, "O nome só pode conter letras"),
	cpf: Yup.string().required("Digite o CPF do proponete"),
	nomePai: Yup.string().max(40),
	nomeReduzido: Yup.string()
		.required("Digite o nome do proponente novamente")
		.min(3, "É necessário ao menos 3 caracteres")
		.max(25, "Não pode exceder 25 caracteres"),
	cmbSexo: Yup.string().required("Por favor selecione o sexo do proponente"),
	nomeMae: Yup.string()
		.required("Digite o nome da mãe do proponente")
		.min(5, "O nome da mãe tem que ter no mínimo 5 letras")
		.max(40, "O nome da mãe tem que ter no máximo 40 letras")
		.matches(
			/[^A-Za-z]/g,
			"Digite o nome e sobrenome da mãe do proponente",
		),
	cmbNacionalidade: Yup.string().required(
		"Por favor selecione a nacionalidade",
	),
	cmbPaisDeNacionalidade: Yup.string(),
	cmbUFNaturalidade: Yup.string(),
	cmbMunicipioNaturalidade: Yup.string(),
	cmbGrauInstrucao: Yup.string().required("Selecione o grau de instrução"),
	pis: Yup.string(),
	cmbTipoDocumento: Yup.string().required(
		"Selecione o tipo de documento de identificação",
	),
	dataNascimento: Yup.string()
		.required()
		.test("date validation", "Data não é valida", (val) => {
			if (!val) {
				return false;
			}

			const date = validateDate({
				value: val,
				subtype: DataSubtypes["date/month/year"],
			});

			if (!date) {
				return false;
			}

			const minLimit = moment();

			minLimit.subtract("18", "years");

			if (minLimit < moment(date)) {
				//Não pode ser menor de idade
				return false;
			}
			return true;
		}),
	identidadeNumeroCnh: Yup.string(),
	dataPrimeiraHabilitacaoCnh: Yup.string().when("cmbTipoDocumento", {
		is: (val: string) => val === "0782",
		then: Yup.string()
			.required("Informe a data da primeira habilitação")
			.test(
				"validate-date",
				"Data da primeira habilitação invalida",
				(val) => {
					if (!val) {
						return false;
					}

					const date = validateDate({
						value: val,
						subtype: DataSubtypes["date/month/year"],
					});

					if (!date) {
						return false;
					}

					const now = new Date();

					if (date > now) {
						//Data da primeira habilitação tem que ser anterior a data de hoje
						return false;
					}
					return true;
				},
			),
		otherwise: Yup.string(),
	}),
	cmbOrgaoEmissorCnh: Yup.string().when("cmbTipoDocumento", {
		is: (val: string) => val === "0782",
		then: Yup.string().required(),
		otherwise: Yup.string(),
	}),
	cmbUFIdentidadeCnh: Yup.string(),
	cmbOrgaoEmissorPassaporte: Yup.string(),
	identidadeNumeroPassaporte: Yup.string(),
	subTipoIdentidadeNumeroRg: Yup.string(),
	cmbOrgaoEmissorRg: Yup.string(),
	cmbUFIdentidadeRg: Yup.string(),

	cmbUFIdentidadePassaporte: Yup.string(),
	paisDeOrigemPassaporte: Yup.string(),
	dataEmissaoIdentidadeCnh: Yup.string().when("cmbTipoDocumento", {
		is: (val: string) => val === "0782",
		then: Yup.string()
			.required()
			.test("validate-date", "Data não é válida", (value) => {
				if (!value) {
					return false;
				}

				const date = validateDate({
					value,
					subtype: DataSubtypes["date/month/year"],
				});

				if (!date) {
					return false;
				}

				const now = new Date();

				if (date > now) {
					//Data de emissão não pode ser maior que a data atual
					return false;
				}

				return true;
			}),
		otherwise: Yup.string(),
	}),

	dataFimValidadeCnh: Yup.string().when("cmbTipoDocumento", {
		is: (val: string) => val === "0782",
		then: Yup.string()
			.required()
			.test("validate-date", "Data não é válida", (value) => {
				if (!value) {
					return false;
				}

				const date = validateDate({
					value,
					subtype: DataSubtypes["date/month/year"],
				});

				if (!date) {
					return false;
				}

				const now = new Date();

				if (date <= now) {
					//Data de validade não pode ser menor ou igual a data atual
					return false;
				}

				return true;
			}),
		otherwise: Yup.string(),
	}),

	dataEmissaoIdentidadePassaporte: Yup.string().when("cmbTipoDocumento", {
		is: (val: string) => val === "0781",
		then: Yup.string()
			.required()
			.test("validate-date", "Data não é válida", (value) => {
				if (!value) {
					return false;
				}

				const date = validateDate({
					value,
					subtype: DataSubtypes["date/month/year"],
				});

				if (!date) {
					return false;
				}

				const now = new Date();

				if (date > now) {
					//Data de emissão não pode ser maior que a data atual
					return false;
				}

				return true;
			}),
		otherwise: Yup.string(),
	}),
	dataFimValidadePassaporte: Yup.string().when("cmbTipoDocumento", {
		is: (val: string) => val === "0781",
		then: Yup.string()
			.required()
			.test("validate-date", "Data não é válida", (value) => {
				if (!value) {
					return false;
				}

				const date = validateDate({
					value,
					subtype: DataSubtypes["date/month/year"],
				});

				if (!date) {
					return false;
				}

				const now = new Date();

				if (date <= now) {
					//Data de validade não pode ser menor que a data atual
					return false;
				}

				return true;
			}),
		otherwise: Yup.string(),
	}),
	dataEmissaoIdentidadeRg: Yup.string().when("cmbTipoDocumento", {
		is: (val: string) => val === "0372102" || val === "03722008",
		then: Yup.string()
			.required()
			.test("validate-date", "Data não é válida", (value) => {
				if (!value) {
					return false;
				}

				const date = validateDate({
					value,
					subtype: DataSubtypes["date/month/year"],
				});

				if (!date) {
					return false;
				}

				const now = new Date();

				if (date > now) {
					//Data de emissão não pode ser maior que a data atual
					return false;
				}

				return true;
			}),
		otherwise: Yup.string(),
	}),
	dataFimValidadeRg: Yup.string().when("cmbTipoDocumento", {
		is: (val: string) => val === "0372102" || val === "03722008",
		then: Yup.string().test(
			"validate-date",
			"Data não é válida",
			(value) => {
				if (!value) {
					return true;
				}

				const dateNumbers = getOnlyDateNumbers(value);

				if (dateNumbers.length === 0) {
					return true;
				}

				const date = validateDate({
					value,
					subtype: DataSubtypes["date/month/year"],
				});

				if (!date) {
					return false;
				}

				const now = new Date();

				if (date <= now) {
					//Data de validade não pode ser menor ou igual a data atual
					return false;
				}

				return true;
			},
		),
		otherwise: Yup.string(),
	}),
	cmbEstadoCivil: Yup.string(),
	cpfConjuge: Yup.string(),
	cmbTpOcupacao: Yup.string().required("Selecione a ocupação"),
	cmbOcupacaoInformal: Yup.string(),
	cmbOcupacaoFormal: Yup.string(),
	cep: Yup.string().required("Digite o CEP"),
	logradouro: Yup.string().required("Digite a rua"),
	cmbTipoLogradouro: Yup.string(),
	complemento: Yup.string(),
	bairro: Yup.string().required("Digite o bairro"),
	numero: Yup.string().required("Digite o número da casa"),
	cmbTipoImovel: Yup.string().required("Selecione o tipo de propriedade"),
	cmbTipoOcupacaoImovel: Yup.string().required(
		"Selecione a ocupação do imóvel",
	),
	comprovanteResidenciaMes: Yup.string()
		.required("Digite o mês do comprovante de residência")
		.test("validate-date", "Data não é válida", function (value) {
			if (!value) {
				return false;
			}

			const date = validateDate({
				value,
				subtype: DataSubtypes.month,
			});

			if (!date) {
				return false;
			}

			const dateYear = validateDate({
				value: this?.parent.comprovanteResidenciaAno,
				subtype: DataSubtypes.year,
			});

			if (!dateYear) {
				return false;
			}

			let dateWithYear = moment(
				`${date.getMonth()}/${dateYear.getFullYear()}`,
				"MM/YYYY",
			).toDate();
			const now = new Date();
			const formatedNow = moment(
				`${now.getMonth() + 1}/${now.getFullYear()}`,
				"MM/YYYY",
			).toDate();

			const minDate = moment(formatedNow).subtract(3, "months").toDate(); //Data limite é 3 meses

			if (dateWithYear > formatedNow || dateWithYear < minDate) {
				//Data de comprovante de renda não pode ser maior que a data atual e nem mais antiga que 3 meses
				return false;
			}

			return true;
		}),
	comprovanteResidenciaAno: Yup.string()
		.required("Digite o ano do comprovante de residência")
		.test("date validation", "Data não é válida", function (value) {
			if (!value) {
				return false;
			}

			const date = validateDate({
				value,
				subtype: DataSubtypes.year,
			});

			if (!date) {
				return false;
			}

			const dateMonth = validateDate({
				value: this?.parent.comprovanteResidenciaMes,
				subtype: DataSubtypes.month,
			});

			if (!dateMonth) {
				return false;
			}

			let dateWithMonth = moment(
				`${dateMonth.getMonth()}/${date.getFullYear()}`,
				"MM/YYYY",
			).toDate();
			const now = new Date();

			const formatedNow = moment(
				`${now.getMonth() + 1}/${now.getFullYear()}`,
				"MM/YYYY",
			).toDate();

			const minDate = moment(formatedNow).subtract(3, "months").toDate(); //Data limite é 3 meses

			if (dateWithMonth > formatedNow || dateWithMonth < minDate) {
				//Data de comprovante de renda não pode ser maior que a data atual e nem mais antiga que 3 meses
				return false;
			}

			return true;
		}),

	dddRes: Yup.string()
		.required("DDD necessário")
		.max(4, "Não pode conter mais de dois numeros"),
	telefoneRes: Yup.string().required("Digite o número do proponente"),
	dddCel: Yup.string(),
	telefoneCel: Yup.string(),

	email: Yup.string().email("Endereço de e-mail inválido"),
	cmbCaracteristicaRenda: Yup.string().required(
		"Selecione a característica da renda",
	),
	cmbCnpj_cpf: Yup.string().when("cmbCaracteristicaRenda", {
		is: (val: string) => val === "6",
		then: Yup.string().required("Selecione o tipo da renda"),
		otherwise: Yup.string(),
	}),
	numeroCpf: Yup.string(),
	numeroCnpj: Yup.string(),

	cmbOcupacao: Yup.string().when("cmbCaracteristicaRenda", {
		is: (val: string) => val === "6",
		then: Yup.string().required("Selecione a ocupação"),
		otherwise: Yup.string(),
	}),
	dataAdmissao: Yup.string().when("cmbCaracteristicaRenda", {
		is: (val: string) => val === "6",
		then: Yup.string()
			.required("Digite a data de admissão")
			.test("validate-date", "Data não é válida", (value) => {
				if (!value) {
					return false;
				}

				const date = validateDate({
					value,
					subtype: DataSubtypes["date/month/year"],
				});

				if (!date) {
					return false;
				}

				const now = new Date();

				if (date > now) {
					//Data de admissão não pode ser maior que a data atual
					return false;
				}

				return true;
			}),
		otherwise: Yup.string(),
	}),
	anoDesligamento: Yup.string().when("dataAdmissao", {
		is: (value: string) => {
			if (!value) {
				return false;
			}

			const date = validateDate({
				value,
				subtype: DataSubtypes["date/month/year"],
			});

			if (!date) {
				return false;
			}

			const minDate = moment().subtract("120", "days").toDate();

			if (minDate > date) {
				//Se a data limite para não ter que renderizar esse input for maior que a data atual
				return false;
			}
			return true;
		},
		then: Yup.string()
			.required("Informe o ano de desligamento")
			.test("date-validation", "Ano inválido", (value) => {
				if (!value) {
					return false;
				}

				const date = validateDate({
					value,
					subtype: DataSubtypes.year,
				});

				if (!date) {
					return false;
				}

				const now = moment().toDate();

				if (date > now) {
					//Data do ano não pode ser maior que ano atual
					return false;
				}

				return true;
			}),
		otherwise: Yup.string(),
	}),
	empregoAnteriorAnos: Yup.string().when("dataAdmissao", {
		is: (value: string) => {
			if (!value) {
				return false;
			}

			const date = validateDate({
				value,
				subtype: DataSubtypes["date/month/year"],
			});

			if (!date) {
				return false;
			}

			const minDate = moment().subtract("120", "days").toDate();

			if (minDate > date) {
				//Se a data limite para não ter que renderizar esse input for maior que a data atual
				return false;
			}
			return true;
		},
		then: Yup.string().required(),
		// .test("validate-date", "Data inválida", function (value) {
		// 	if (!value) {
		// 		return false;
		// 	}

		// 	const date = validateDate({
		// 		value,
		// 		subtype: DataSubtypes.year,
		// 	});

		// 	if (!date) {
		// 		return false;
		// 	}
		// 	const dateMonth = validateDate({
		// 		value: this?.parent.comprovanteResidenciaMes,
		// 		subtype: DataSubtypes.month,
		// 	});

		// 	if (!dateMonth) {
		// 		return false;
		// 	}

		// 	let dateWithMonth = moment(
		// 		`${dateMonth.getMonth()}/${date.getFullYear()}`,
		// 		"MM/YYYY",
		// 	).toDate();
		// 	const now = new Date();

		// 	const formatedNow = moment(
		// 		`${now.getMonth() + 1}/${now.getFullYear()}`,
		// 		"MM/YYYY",
		// 	).toDate();

		// 	const now = moment().toDate();

		// 	if (date > now) {
		// 		//Data do ano for maior que agora
		// 		return false;
		// 	}

		// 	return true;
		// }),
		otherwise: Yup.string(),
	}),
	empregoAnteriorMeses: Yup.string().when("dataAdmissao", {
		is: (value: string) => {
			if (!value) {
				return false;
			}

			const date = validateDate({
				value,
				subtype: DataSubtypes["date/month/year"],
			});

			if (!date) {
				return false;
			}

			const minDate = moment().subtract("120", "days").toDate();

			if (minDate > date) {
				//Se a data limite para não ter que renderizar esse input for maior que a data atual
				return false;
			}
			return true;
		},
		then: Yup.string().required(),
		otherwise: Yup.string(),
	}),
	valorRendaBruta: Yup.string().when("cmbCaracteristicaRenda", {
		is: (val: string) => val === "6",
		then: Yup.string().required("Digite a renda bruta"),
		otherwise: Yup.string(),
	}),
	valorRendaLiquida: Yup.string().when("cmbCaracteristicaRenda", {
		is: (val: string) => val === "6",
		then: Yup.string().required("Digite a renda líquida"),
		otherwise: Yup.string(),
	}),
	cmbComprovanteRenda: Yup.string().when("cmbCaracteristicaRenda", {
		is: (val: string) => val === "6",
		then: Yup.string().required(
			"Selecione o documento de comprovante de renda",
		),
		otherwise: Yup.string(),
	}),
	dtRefCompr: Yup.string().when("cmbCaracteristicaRenda", {
		is: (val: string) => val === "6",
		then: Yup.string()
			.required("Digite a data do comprovante de renda")
			.test("validate-date", "Data não é válida", (value) => {
				if (!value) {
					return false;
				}
				const date = validateDate({
					value,
					subtype: DataSubtypes["month/year"],
				});

				if (!date) {
					return false;
				}
				const now = new Date();
				const nowMoment = moment(
					`${now.getMonth() + 1}/${now.getFullYear()}`,
					"MM/YYYY",
				).toDate();

				const dateLimit = moment(nowMoment)
					.subtract(3, "months")
					.toDate(); //Data limite é 3 meses

				if (date > nowMoment || date < dateLimit) {
					//Data de emissão do comprovante não pode ser maior que a data atual e nem mais antiga que 3 meses
					return false;
				}

				return true;
			}),
		otherwise: Yup.string(),
	}),
	iImposRendaRetFonte: Yup.string().when("dtRefCompr", {
		is: (val: string) => val === "64522009",
		then: Yup.string().required("Digite o IRPF"),
		otherwise: Yup.string(),
	}),
	nomeAtividade: Yup.string(),
	dataDeInicio: Yup.string(),

	rendaLiquidaMensal: Yup.string(),

	docCompAtivNComp: Yup.string(),
	proof_doc: Yup.string(),
	iNumSemDepRenda: Yup.string().required(
		"Digite o número de dependentes sem renda",
	),
});
